/**
 * v0.6 Stage F.1 — small persistent-boolean hook.
 *
 * Stores a boolean flag in `window.localStorage` keyed on an opaque
 * caller-supplied string. Reads on mount (synchronous, in the
 * `useState` initializer) so the first render has the persisted
 * value with no flash-of-default. Writes happen inside the toggle
 * callback, wrapped in try/catch — private browsing / quota errors
 * fall back to in-memory state without surfacing a crash to the
 * reviewer.
 *
 * Intended consumers
 * ------------------
 *   * F.1 — recommended-tier section collapse state, keyed per run_id.
 *   * F.3 — per-card collapse state, keyed per hit_id.
 *
 * Both surfaces want the same shape: a `boolean` plus a stable
 * `toggle` callback, persisted under a caller-chosen key so two
 * consumers can't collide.
 *
 * SSR / non-browser safety
 * ------------------------
 * Guards against `typeof window === 'undefined'` so the hook is
 * importable from server-rendered contexts (Vite SSR, Vitest's
 * jsdom environment). In a non-browser context the hook degrades
 * to in-memory state (whatever `defaultValue` is on first call).
 *
 * Key-change semantics
 * --------------------
 * If `key` changes between renders the hook re-reads `localStorage`
 * inside a `useEffect`, picking up the value for the new key. This
 * matters because the HITL queue's `run_id` arrives via props (the
 * parent fetches hits async) — the first render may have a fallback
 * key while the page is loading, and the real `run_id`-keyed value
 * has to take effect once hits land. Without re-read-on-key-change
 * the state stays bound to the fallback key for the rest of the
 * session and the reviewer's expand state doesn't persist across
 * reloads.
 *
 * The re-read happens in `useEffect`, so the value resolves on the
 * second render after the key flip — fine for collapse state where
 * a one-frame default is acceptable.
 */
import { useCallback, useEffect, useState } from 'react';

export function useLocalStorageBoolean(
  key: string,
  defaultValue: boolean,
): [boolean, () => void] {
  const [value, setValue] = useState<boolean>(() => readStored(key, defaultValue));

  // Re-read when the key changes (see "Key-change semantics" above).
  // ``defaultValue`` is included in deps for lint hygiene but in
  // practice consumers pass a stable literal (the F.1 call site
  // passes ``true``), so the effect runs once per mount + once per
  // key change. If a consumer ever passes a derived/changing
  // ``defaultValue``, the re-read fires on every value change —
  // probably what they want for that case.
  useEffect(() => {
    setValue(readStored(key, defaultValue));
  }, [key, defaultValue]);

  const toggle = useCallback(() => {
    setValue((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(key, String(next));
      } catch {
        // Quota exceeded or storage disabled. In-memory state still
        // updates — the UI stays responsive within the session,
        // it just won't persist across reloads.
      }
      return next;
    });
  }, [key]);

  return [value, toggle];
}

function readStored(key: string, defaultValue: boolean): boolean {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const stored = window.localStorage.getItem(key);
    if (stored === null) return defaultValue;
    return stored === 'true';
  } catch {
    // Private-browsing modes can throw on `localStorage` access
    // (Safari ITP, some embedded webviews). Fall back to the default.
    return defaultValue;
  }
}
